<template>
  <StackLayout :class="classes" v-if="show" 
    :role="link ? 'button' : null"
    :tabindex="link ? 0 : -1"
    :aria-label="link ? name + ' - ' + number : null"
    @keydown="handleKeydown"
    @tap="tap"
    @click="click"
    @touch="touch"
  >
    <AgCard avatar :caption="name" :background="background">
      <AgAvatar :color="image ? 'white' : null" :touchable="touchable" :edit="isEditable" :loading="loading" slot="avatar" :image="image" @edit="editPicture" @tap="showPicture" @click="showPicture">
        <AgIcon v-if="!image" set="agila" :type="iconType" :size="iconSize" />
      </AgAvatar>
      <FlexboxLayout class="number" justifyContent="center">
        <Label :text="number" />
      </FlexboxLayout>
      <slot name="default" />
    </AgCard>
  </StackLayout>
</template>

<script>

import env from '@/helpers/env'
import media from '@/helpers/media'
import AnimalService from '@/services/animal'

export default {
  props: {
    animal: {
      type: Object
    },
    link: {
      type: Boolean
    },
    editable: {
      type: Boolean
    },
    touchable: {
      type: Boolean
    },
    background: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      active: null,
      loading: null,
      isMobile: env.isAndroid || env.isIos
    }
  },
  computed: {
    classes () {
      let classes = ['animal']

      if (this.active) {
        classes.push('active')
      }

      if (this.link) {
        classes.push('link')
      }

      return classes.join(' ')
    },
    name () {
      return this.animal?.name || '[name]'
    },
    number () {
      return 'Vertrags-Nr. ' + this.animal?.contract.police_id + String(this.animal?.contract.id).padStart(2, '0')
    },
    iconType () {
      return this.animal?.type === 'HUND' ? 'dog' : 'cat'
    },
    iconSize () {
      return this.animal?.type === 'HUND' ? 65 : 60
    },
    image () {
      return this.animal?.image || ''
    },
    show () {
      return !(this.animal?.hide || false)
    },
    isEditable () {
      return !this.loading && this.editable
    }
  },
  methods: {
    tap (event) {
      if (this.link) {
        this.$emit('tap', event)
      }
    },
    click (event) {
      if (this.link) {
        this.$emit('click', event)
      }
    },
    touch (event) {
      if (this.link) {
        if (event.action === 'down') {
          this.active = true
        } else if (event.action === 'up' || event.action === 'cancel') {
          this.active = false
        }
      }
    },
    showPicture () {
      if (this.touchable && this.animal.image) {
        media.view(this.animal.identifier + '.jpg', this.animal.image)
      }
    },
    editPicture () {
      const sources = [media.sources.picker]
      const options = {
        size: 600,
        types: ['image/jpeg'],
        remove: false,
        onSelect: () => {
          this.loading = true
        }
      }

      if (this.isMobile) {
        sources.push(media.sources.camera)
      }

      media.choose(options, sources).then(images => {
        if (images?.length > 0) {
          return AnimalService.setImage(this.animal.identifier, images[0].data ).then(() => {
            this.$set(this.animal, 'image', images[0].data)
          }).catch(error => {
            if (error.handler) {
              error.handler(this)
            }
          })
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleKeydown(event) {
      
      if (this.link && (event.key === 'Enter' || event.key === ' ')) {        
        if (this.$listeners.keydown) {
          this.$emit('keydown', event);
          return;
        }
        
        event.preventDefault();
        // Simulate click/tap event for keyboard users
        this.click(event);
        this.tap(event);
      } 
    }
  },
  mounted() {
    // Add native keyboard event listener for web (similar to Button.vue)
    if (typeof window !== 'undefined' && this.$el) {
      this.$el.addEventListener('keydown', (event) => {
        this.handleKeydown(event);
      });
    }
  }
}

</script>

<style scoped lang="scss">

.animals.solo .animal {
  width: 100%;
  border-right-width: 0;
}

.animal {
  .number {
    & > * {
      text-align: center;
      color: white;
      background-color: #004e9e;
    }
  }

  &.link {
    &:active::v-deep .wrapper,
    &.active::v-deep .wrapper {
      background-color: #004e9e;
      color: white;
    }

    &:active::v-deep .wrapper .caption,
    &:active::v-deep .wrapper .card-text,
    &.active::v-deep .wrapper .caption,
    &.active::v-deep .wrapper .card-text {
      color: white;
    }

    &:active::v-deep .avatar .circle,
    &.active::v-deep .avatar .circle {
      border-color: #004e9e;
    }

    &:active .number > *,
    &.active .number > * {
      color: #004e9e;
      background-color: white;
    }
  }
}

.ns-ios,
.ns-android {
  .animal {
    width: 280;

    .number > * {
      padding: 3 6;
      border-radius: 4;
      margin-bottom: 10;
    }
  }
}

html {
  .animal {
    width: 250px;
    min-width: 250px;

    &::v-deep .button .text {
      width: 144px;
    }

    &.link {
      cursor: pointer;
      
      &:focus {
        outline: 3px solid #FFD448;
        outline-offset: 2px;
        border-radius: 8px;
      }
    }

    .number > * {
      padding: 3px 6px;
      border-radius: 4px;
      margin-bottom: 10px;
    }
  }
}

</style>